import type { Vehicle } from '../sim/vehicle';

/** Telemetry overlay (hidden unless ?debug): per-wheel load/slip/surface, engine, g-forces. */
export class DebugOverlay {
  readonly el: HTMLDivElement;
  private last = 0;
  extra: () => string = () => '';

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'debug-overlay';
    parent.appendChild(this.el);
  }

  update(v: Vehicle, now: number): void {
    if (now - this.last < 100) return;
    this.last = now;
    const t = v.telemetry;
    const names = ['FL', 'FR', 'RL', 'RR'];
    const rows = v.wheels
      .map((w, i) => {
        const deg = (w.slipAngle * 180) / Math.PI;
        return `${names[i]} ${w.contact ? w.surface.padEnd(7) : 'air    '} Fz ${(w.load / 1000).toFixed(2).padStart(5)}kN  κ ${w.slipRatio.toFixed(2).padStart(6)}  α ${deg.toFixed(1).padStart(6)}°  ω ${w.omega.toFixed(0).padStart(4)}  comp ${(w.compression * 1000).toFixed(0).padStart(3)}mm`;
      })
      .join('\n');
    const d = v.drivetrain;
    this.el.textContent =
      `${(t.speed * 3.6).toFixed(1)} km/h  gear ${t.gear}  ${t.rpm.toFixed(0)} rpm  boost ${(t.boost * 100).toFixed(0)}%  clutch ${(d.clutchEngage * 100).toFixed(0)}%  tc ${(d.tcFactor * 100).toFixed(0)}%\n` +
      `g long ${t.gLong.toFixed(2)}  lat ${t.gLat.toFixed(2)}  vert ${t.gVert.toFixed(2)}  β ${((t.bodySlipAngle * 180) / Math.PI).toFixed(1)}°  yaw ${t.yawRate.toFixed(2)} rad/s\n` +
      `in: steer ${v.applied.steer.toFixed(2)} thr ${v.applied.throttle.toFixed(2)} brk ${v.applied.brake.toFixed(2)} hb ${v.applied.handbrake.toFixed(2)}\n` +
      rows +
      '\n' +
      this.extra();
  }
}

/** CSV telemetry recorder used by tests and by ?debug sessions. */
export class TelemetryRecorder {
  private rows: string[] = [];
  readonly header = 'time,speed,rpm,gear,throttle,brake,steer,handbrake,gLong,gLat,yawRate,beta,x,y,z,FLload,FRload,RLload,RRload,FLslip,FRslip,RLslip,RRslip,FLalpha,FRalpha,RLalpha,RRalpha';

  record(time: number, v: Vehicle): void {
    const t = v.telemetry;
    const p = v.body.translation();
    const w = v.wheels;
    this.rows.push(
      [time, t.speed, t.rpm, t.gear, t.throttle, t.brake, t.steer, v.applied.handbrake, t.gLong, t.gLat, t.yawRate, t.bodySlipAngle, p.x, p.y, p.z, ...w.map((x) => x.load), ...w.map((x) => x.slipRatio), ...w.map((x) => x.slipAngle)]
        .map((n) => (typeof n === 'number' ? Number(n.toFixed(4)) : n))
        .join(','),
    );
  }

  csv(): string {
    return this.header + '\n' + this.rows.join('\n') + '\n';
  }
}
